// Statuses where submitters can edit/delete
const SUBMITTER_EDITABLE_STATUSES = [0, 1, 3, 4] // Draft, Submitted, Rejected, Action Required
// Statuses where approvers can edit
const APPROVER_EDITABLE_STATUSES = [1, 2, 3, 4] // Submitted, Approved, Rejected, Action Required

export const canEditClaim = (isSubmissionVersion, status) => {
  if (isSubmissionVersion) {
    return SUBMITTER_EDITABLE_STATUSES.includes(status)
  }
  return APPROVER_EDITABLE_STATUSES.includes(status)
}

export const canDeleteClaim = (isSubmissionVersion, status) => {
  if (isSubmissionVersion) {
    return SUBMITTER_EDITABLE_STATUSES.includes(status)
  }
  return false
}

export const getDisabledClaimTooltip = (action) => {
  return `Cannot ${action} a claim in its current status`
}

const DRAFT_STATUSES = [0]
const APPROVE_STATUSES = [1, 2, 3, 4] // Submitted, Approved, Rejected, Action Required
const PAYMENT_STATUSES = [5, 6, 7, 8] // Ready to Pay, Posted, Paid, TOIL Agreed

export const getDisabledStatusesFromPermissions = (permissions, claimRecipientUserIds) => {
  const recipientIds = Array.isArray(claimRecipientUserIds)
    ? claimRecipientUserIds
    : [claimRecipientUserIds]

  const disabledStatuses = [...DRAFT_STATUSES]
  const tooltips = {}

  DRAFT_STATUSES.forEach((s) => {
    tooltips[s] = 'Claims cannot be changed to draft status'
  })

  if (!permissions || permissions.length === 0 || recipientIds.length === 0 || recipientIds.every((id) => !id)) {
    APPROVE_STATUSES.forEach((s) => {
      disabledStatuses.push(s)
      tooltips[s] = 'You do not have permission to approve claims for these claim recipients'
    })
    PAYMENT_STATUSES.forEach((s) => {
      disabledStatuses.push(s)
      tooltips[s] = 'You do not have permission to process payments for these claim recipients'
    })
    return { disabledStatuses, tooltips }
  }

  const permissionMap = new Map(permissions.map((p) => [String(p.claimRecipientUserId), p]))

  let allCanApprove = true
  let allCanProcessPayments = true

  for (const recipientId of recipientIds) {
    const perm = permissionMap.get(String(recipientId))
    if (!perm) {
      allCanApprove = false
      allCanProcessPayments = false
      break
    }
    if (!perm.canApprove) allCanApprove = false
    if (!perm.canProcessPayments) allCanProcessPayments = false
  }

  const isSingle = recipientIds.length === 1
  const recipientLabel = isSingle ? 'this claim recipient' : 'these claim recipients'

  if (!allCanApprove) {
    APPROVE_STATUSES.forEach((s) => {
      disabledStatuses.push(s)
      tooltips[s] = `You do not have permission to approve claims for ${recipientLabel}`
    })
  }

  if (!allCanProcessPayments) {
    PAYMENT_STATUSES.forEach((s) => {
      disabledStatuses.push(s)
      tooltips[s] = `You do not have permission to process payments for ${recipientLabel}`
    })
  }

  return { disabledStatuses, tooltips }
}

const ALL_STATUSES = [0, 1, 2, 3, 4, 5, 6, 7, 8]
const POSTED_PAID_ALLOWED = [6, 7, 8]

export const getDisabledStatusesFromCurrentStatuses = (currentStatuses) => {
  const disabledStatuses = []
  const tooltips = {}

  if (!currentStatuses || currentStatuses.length === 0) {
    return { disabledStatuses, tooltips }
  }

  const hasPostedOrPaid = currentStatuses.some((s) => Number(s) >= 6)
  const allReadyToPayOrAbove = currentStatuses.every((s) => [5, 6, 7, 8].includes(Number(s)))

  if (hasPostedOrPaid) {
    ALL_STATUSES.forEach((s) => {
      if (!POSTED_PAID_ALLOWED.includes(s)) {
        disabledStatuses.push(s)
        tooltips[s] = 'Posted or paid claims can only be changed between Posted, Paid and TOIL Agreed'
      }
    })
  } else if (!allReadyToPayOrAbove) {
    POSTED_PAID_ALLOWED.forEach((s) => {
      disabledStatuses.push(s)
      tooltips[s] = 'Claims must be in Ready to Pay status before they can be changed to Posted, Paid or TOIL Agreed'
    })
  }

  return { disabledStatuses, tooltips }
}

export const mergeDisabledStatuses = (...sources) => {
  const disabledSet = new Set()
  const tooltips = {}

  for (const source of sources) {
    if (!source) continue
    for (const s of source.disabledStatuses || []) {
      disabledSet.add(s)
      if (!tooltips[s] && source.tooltips?.[s]) {
        tooltips[s] = source.tooltips[s]
      }
    }
  }

  return { disabledStatuses: [...disabledSet], tooltips }
}
